import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { staffApi } from "../../shared/api/staff";
import { rolesApi, rolesQueryKeys } from "../../shared/api/roles";
import { apiError } from "../../shared/api/client";
import type { StaffInviteRequest, StaffInviteResponse, StaffRole } from "../../shared/types/staff";
import { staffRoleLabel, systemRoleLabel, systemRoleDescription } from "../../shared/format";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import { InviteLinkBox } from "./components/InviteLinkBox";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_ROLES: Record<StaffRole, string[]> = {
  DOCTOR: ["doctor"],
  NURSE: ["receptionist"],
  RECEPTIONIST: ["receptionist"],
  MANAGER: ["clinic_owner"],
  OWNER: ["clinic_owner"],
  OTHER: ["receptionist"],
};

export function UserCreatePage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [staffRole, setStaffRole] = useState<StaffRole>("DOCTOR");
  const [roleCodes, setRoleCodes] = useState<string[]>(DEFAULT_ROLES.DOCTOR);
  const [title, setTitle] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [prescriber, setPrescriber] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<StaffInviteResponse | null>(null);

  const rolesQuery = useQuery({
    queryKey: rolesQueryKeys.list(),
    queryFn: () => rolesApi.list(),
  });

  const invite = useMutation({
    mutationFn: (req: StaffInviteRequest) => staffApi.invite(req),
    onSuccess: (res) => setResult(res),
    onError: (e) => setErrors({ _form: apiError(e).detail }),
  });

  function onRoleChange(next: StaffRole) {
    setStaffRole(next);
    setRoleCodes(DEFAULT_ROLES[next]);
  }

  function toggleRoleCode(code: string) {
    setRoleCodes((cur) =>
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
    );
  }

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!fullName.trim()) next.fullName = "Họ và tên không được để trống";
    if (!email.trim()) next.email = "Email không được để trống";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Email không hợp lệ";
    if (phone && !/^(\+?84|0)\d{9,10}$/.test(phone)) next.phone = "Số điện thoại không hợp lệ";
    if (roleCodes.length === 0) next.roleCodes = "Chọn ít nhất một vai trò hệ thống";
    if (staffRole === "DOCTOR" && !specialty.trim()) next.specialty = "Chuyên khoa không được để trống";
    return next;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Defence in depth (#18681). The save button carries disabled={invite.isPending}, but a
    // <form> is also submitted by pressing Enter in a field, and that path never consults the
    // button. Playwright against the deployed build shows Chrome does NOT submit on Enter from
    // the date/time inputs, so this guard is not what fixed #18681 and nothing observed in
    // production needed it — it is here so the next single-line text field added to this form
    // cannot quietly reintroduce a double submit.
    if (invite.isPending) return;
    setErrors({});
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    invite.mutate({
      fullName,
      email,
      phone: phone || null,
      staffRole,
      roleCodes,
      primaryBranchId: null,
      asDoctor:
        staffRole === "DOCTOR"
          ? { title: title || null, specialty, departmentId, bio: null, prescriber }
          : null,
    });
  }

  if (result) {
    return (
      <div>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
              <CheckCircle2 className="size-5 text-emerald-600" />
              Đã tạo lời mời
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Đã tạo hồ sơ cho <strong className="text-foreground">{result.email}</strong>. Hãy chia
              sẻ link kích hoạt bên dưới — link chỉ hiển thị một lần.
            </p>
          </div>
        </div>
        <InviteLinkBox link={result.inviteLink} />
        <div className="mt-6 flex flex-wrap gap-2">
          <Button asChild variant="secondary">
            <Link to="/settings/users">
              <ArrowLeft />
              Về danh sách nhân viên
            </Link>
          </Button>
          <Button asChild>
            <Link to={`/settings/users/${result.staffProfileId}`}>
              Xem hồ sơ
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <h1 className="text-xl font-bold text-foreground">Mời nhân viên mới</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tạo hồ sơ + phát hành link kích hoạt tài khoản. Nhân viên sẽ đặt mật khẩu và bật MFA khi
            mở link. Link có hiệu lực 7 ngày.
          </p>
        </div>
        <Button asChild variant="secondary" className="shrink-0">
          <Link to="/settings/users">
            <ArrowLeft />
            Danh sách
          </Link>
        </Button>
      </div>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Thông tin nhân viên</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fullName">Họ &amp; tên *</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoFocus
                aria-invalid={errors.fullName ? true : undefined}
              />
              {errors.fullName && (
                <span className="text-xs text-destructive">{errors.fullName}</span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ten@phongkhamcuaban.vn"
                aria-invalid={errors.email ? true : undefined}
              />
              {errors.email && <span className="text-xs text-destructive">{errors.email}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="phone">Số điện thoại</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0967369125"
                aria-invalid={errors.phone ? true : undefined}
              />
              {errors.phone && <span className="text-xs text-destructive">{errors.phone}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="staffRole">Vị trí *</Label>
              <Select value={staffRole} onValueChange={(v) => onRoleChange(v as StaffRole)}>
                <SelectTrigger id="staffRole">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(staffRoleLabel) as StaffRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {staffRoleLabel[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vai trò hệ thống</CardTitle>
            <CardDescription>
              Xác định <strong className="text-foreground">quyền hạn</strong> trong hệ thống — khác
              với "Vị trí" là thông tin nhân sự. Bạn có thể thay đổi sau ở trang chi tiết nhân viên.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {rolesQuery.isLoading && (
              <p className="text-sm text-muted-foreground">Đang tải danh sách vai trò…</p>
            )}
            {rolesQuery.data && (
              <div className="flex flex-col gap-2">
                {rolesQuery.data.map((r) => {
                  const checked = roleCodes.includes(r.code);
                  const label = systemRoleLabel[r.code] ?? r.name;
                  const desc = systemRoleDescription[r.code] ?? r.description ?? "";
                  return (
                    <label
                      key={r.code}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border px-3.5 py-2.5 transition-colors ${
                        checked ? "border-primary bg-primary/5" : "border-input bg-card"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRoleCode(r.code)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-semibold text-foreground">{label}</div>
                        {desc && (
                          <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {errors.roleCodes && (
              <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errors.roleCodes}
              </div>
            )}
          </CardContent>
        </Card>

        {staffRole === "DOCTOR" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hồ sơ bác sĩ</CardTitle>
              <CardDescription>
                Thông tin chuyên môn — hiển thị trong lịch hẹn và đơn thuốc.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="title">Học vị / Chức danh</Label>
                <Input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="VD: BS.CKII, ThS.BS, PGS.TS"
                  maxLength={64}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="specialty">Chuyên khoa *</Label>
                <Input
                  id="specialty"
                  type="text"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="VD: Nội tiêu hoá, Tim mạch"
                  maxLength={128}
                  aria-invalid={errors.specialty ? true : undefined}
                />
                {errors.specialty && (
                  <span className="text-xs text-destructive">{errors.specialty}</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="departmentId">Khoa</Label>
                <DepartmentSelect
                  id="departmentId"
                  value={departmentId}
                  onChange={setDepartmentId}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                  <Checkbox
                    checked={prescriber}
                    onCheckedChange={(v) => setPrescriber(v === true)}
                  />
                  Được kê đơn thuốc
                </label>
                <span className="text-xs text-muted-foreground">
                  Bác sĩ có quyền lập và ký đơn thuốc cho bệnh nhân.
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {errors._form && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errors._form}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button asChild variant="secondary">
            <Link to="/settings/users">Hủy</Link>
          </Button>
          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending ? "Đang tạo…" : "Tạo + Phát hành link"}
          </Button>
        </div>
      </form>
    </div>
  );
}
